import React from "react";
import { IDynamicFormFieldProps } from "./definitions";
import { FormField } from "@amzn/awsui-components-react";
import {
  ControlledCheckbox,
  ControlledDatePicker,
  ControlledInput,
  ControlledMultiselect,
  ControlledRadioGroup,
  ControlledSelect,
  ControlledTextarea,
} from "@amzn/react-hook-form-polaris";
import ControlledMonetaryInput from "components/controlled-monetary-input";
import { get } from "react-hook-form";
import { useGaiaObjectUiContent } from "features/ui-content";

const DynamicFormField: React.FC<IDynamicFormFieldProps> = ({
  attributeSchema,
  formState,
  control,
  namePrefix,
  gaiaObjectType,
  filteredOptions,
  mode,
  setValue,
}: IDynamicFormFieldProps) => {
  const uiContent: any = useGaiaObjectUiContent(gaiaObjectType);
  const label = attributeSchema.required ? (
    attributeSchema.fieldName
  ) : (
    <>
      {attributeSchema.fieldName} - <i>optional</i>
    </>
  );

  switch (attributeSchema.formFieldType) {
    case "Text":
    case "Number":
    case "ReadOnly":
      return (
        <FormField
          key={attributeSchema.attributeName}
          label={label}
          errorText={get(
            formState?.errors,
            `${
              (namePrefix ? `${namePrefix}.` : "") +
              attributeSchema.attributeName
            }.message`,
          )}
          description={attributeSchema.description}
        >
          <ControlledInput
            key={attributeSchema.attributeName}
            name={
              (namePrefix ? `${namePrefix}.` : "") +
              attributeSchema.attributeName
            }
            control={control}
            readOnly={attributeSchema.formFieldType === "ReadOnly"}
          />
        </FormField>
      );
    case "Dropdown": {
      const dropdown =
        attributeSchema.attributeName === "owner"
          ? mode === "edit"
            ? "teamAndCurResourceGuestOwnerOptions"
            : "ownerOptions"
          : `${attributeSchema.attributeName}Options`;
      const baseDropdownList =
        filteredOptions ??
        uiContent[dropdown] ??
        attributeSchema.dropdownValueList ??
        [];

      const hasEmptyOption = baseDropdownList.some(
        (option: any) => option.value === "",
      );

      const dropdownList =
        attributeSchema.required || hasEmptyOption
          ? baseDropdownList
          : [{ label: "\u00A0", value: "" }, ...baseDropdownList];

      const isEntityField = attributeSchema.attributeName === "entity";
      const fieldName =
        (namePrefix ? `${namePrefix}.` : "") + attributeSchema.attributeName;

      return (
        <FormField
          key={attributeSchema.attributeName}
          label={label}
          errorText={get(
            formState?.errors,
            `${
              (namePrefix ? `${namePrefix}.` : "") +
              attributeSchema.attributeName
            }.message`,
          )}
          description={attributeSchema.description}
        >
          <ControlledSelect
            key={attributeSchema.attributeName}
            name={fieldName}
            options={dropdownList}
            control={control}
            filteringType="auto"
            onChange={
              isEntityField && setValue
                ? (detail) => {
                    const selectedOption = detail.detail.selectedOption;
                    if (selectedOption) {
                      setValue(fieldName, selectedOption.value || "");
                      const entityCodeFieldName = `${namePrefix ? `${namePrefix}.` : ""}entityCode`;
                      setValue(
                        entityCodeFieldName,
                        selectedOption.description || "",
                      );
                    }
                  }
                : undefined
            }
          />
        </FormField>
      );
    }
    case "Date":
      return (
        <FormField
          key={attributeSchema.attributeName}
          label={label}
          errorText={get(
            formState?.errors,
            `${
              (namePrefix ? `${namePrefix}.` : "") +
              attributeSchema.attributeName
            }.message`,
          )}
          description={attributeSchema.description}
        >
          <ControlledDatePicker
            key={attributeSchema.attributeName}
            name={
              (namePrefix ? `${namePrefix}.` : "") +
              attributeSchema.attributeName
            }
            control={control}
            placeholder="YYYY/MM/DD"
          />
        </FormField>
      );
    case "Checkbox":
      return (
        <FormField
          key={attributeSchema.fieldName}
          errorText={get(
            formState?.errors,
            `${
              (namePrefix ? `${namePrefix}.` : "") +
              attributeSchema.attributeName
            }.message`,
          )}
        >
          <ControlledCheckbox
            name={
              (namePrefix ? `${namePrefix}.` : "") +
              attributeSchema.attributeName
            }
            control={control}
            description={attributeSchema.description}
          >
            {attributeSchema.required ? (
              <b>{attributeSchema.fieldName}</b>
            ) : (
              <b>
                {attributeSchema.fieldName} - <i>optional</i>
              </b>
            )}
          </ControlledCheckbox>
        </FormField>
      );
    case "Multiselect": {
      const multiselect = `${attributeSchema.attributeName}Options`;
      const multiselectList =
        uiContent[multiselect] ?? attributeSchema.dropdownValueList;
      return (
        <FormField
          key={attributeSchema.attributeName}
          label={label}
          errorText={get(
            formState?.errors,
            `${
              (namePrefix ? `${namePrefix}.` : "") +
              attributeSchema.attributeName
            }.message`,
          )}
          description={attributeSchema.description}
        >
          <ControlledMultiselect
            key={attributeSchema.attributeName}
            name={
              (namePrefix ? `${namePrefix}.` : "") +
              attributeSchema.attributeName
            }
            options={multiselectList}
            control={control}
            filteringType="auto"
          />
        </FormField>
      );
    }
    case "Textarea": {
      const isDescriptionInDraftMode =
        attributeSchema.fieldName === "Description" && mode === "draft";

      return (
        <FormField
          key={attributeSchema.attributeName}
          label={label}
          errorText={get(
            formState?.errors,
            `${
              (namePrefix ? `${namePrefix}.` : "") +
              attributeSchema.attributeName
            }.message`,
          )}
          description={attributeSchema.description}
        >
          <ControlledTextarea
            key={attributeSchema.attributeName}
            name={
              (namePrefix ? `${namePrefix}.` : "") +
              attributeSchema.attributeName
            }
            control={control}
            rows={isDescriptionInDraftMode ? 8 : undefined}
          />
        </FormField>
      );
    }
    case "RadioGroup": {
      const radiogroup = `${attributeSchema.attributeName}Options`;
      const radiogroupList =
        uiContent[radiogroup] ?? attributeSchema.dropdownValueList;
      return (
        <FormField
          key={attributeSchema.attributeName}
          label={label}
          errorText={get(
            formState?.errors,
            `${
              (namePrefix ? `${namePrefix}.` : "") +
              attributeSchema.attributeName
            }.message`,
          )}
          description={attributeSchema.description}
        >
          <ControlledRadioGroup
            key={attributeSchema.attributeName}
            name={
              (namePrefix ? `${namePrefix}.` : "") +
              attributeSchema.attributeName
            }
            items={radiogroupList}
            control={control}
          />
        </FormField>
      );
    }
    case "MonetaryInput":
      // if setting FormFieldType to be "MonetaryInput", ValueType should be set to "amount" for proper viewing in overview pages
      return (
        <ControlledMonetaryInput
          key={attributeSchema.attributeName}
          name={
            (namePrefix ? `${namePrefix}.` : "") + attributeSchema.attributeName
          }
          control={control}
          title={label}
          currencySign="$"
          formatWithCommas={true}
        />
      );

    default:
      return <div />;
  }
};

export default DynamicFormField;
